/**
 * The job register's columns and card, shared by /well-testing/jobs and the
 * "Jobs on this well" section of a well's page (WT-2, fieldiq-api#1245).
 *
 * The status rail and badge come from WT_JOB_STATUS_SKIN; an unknown status
 * is the slate `—` badge on a transparent rail. Dates are calendar days.
 */
import React from 'react';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { wtDay, wtJobStatusSkin } from '../../lib/well-testing';
import type { WtJob } from '../../types/well-testing';
import { WtBadge, type WtColumn } from './WtBits';

export const plannedRange = (from: string | null, to: string | null): string =>
  !from && !to ? '—' : `${wtDay(from)}${to ? ` → ${wtDay(to)}` : ''}`;

/** Every column; `withWell: false` drops the well column (on a well's own page it is that well). */
export function jobColumns({ withWell = true }: { withWell?: boolean } = {}): WtColumn<WtJob>[] {
  const cols: WtColumn<WtJob>[] = [
    {
      key: 'number',
      header: 'Job',
      render: (j) => (
        <>
          <p className="whitespace-nowrap font-mono font-extrabold text-white">{j.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{j.jobKindLabel || j.jobKind || '—'}</p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (j) => j.customerName || '—' },
  ];
  if (withWell)
    cols.push({
      key: 'well',
      header: 'Well',
      render: (j) => (
        <>
          <p className="min-w-[170px]">{j.wellName || '—'}</p>
          <p className="mt-0.5 whitespace-nowrap font-mono text-xs text-fieldiq-muted">{j.wellUwi || j.wellApiNumber || '—'}</p>
        </>
      ),
    });
  cols.push(
    { key: 'planned', header: 'Planned', render: (j) => <span className="block min-w-[120px]">{plannedRange(j.plannedStartOn, j.plannedEndOn)}</span> },
    { key: 'supervisor', header: 'Supervisor', render: (j) => j.supervisorName || '—' },
    { key: 'crew', header: 'Crew', align: 'right', render: (j) => displayValue(j.crewCount ?? undefined) },
    // Count and last day in ONE cell: as two columns the register outgrew the
    // panel at 1280 and clipped the status badge (the Wells bug, again).
    {
      key: 'logs',
      header: 'Logs',
      render: (j) => (
        <>
          <p>{displayValue(j.shiftLogCount ?? undefined)}</p>
          <p className="mt-0.5 whitespace-nowrap text-xs text-fieldiq-muted">{j.lastLogDate ? `Last ${wtDay(j.lastLogDate)}` : '—'}</p>
        </>
      ),
    },
    { key: 'status', header: 'Status', render: (j) => <WtBadge skin={wtJobStatusSkin(j.status)} value={j.status} /> },
  );
  return cols;
}

export function JobCard({ job }: { job: WtJob }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-extrabold text-white">{job.jobNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {job.customerName || '—'} · {job.wellName || '—'}
          </p>
        </div>
        <WtBadge skin={wtJobStatusSkin(job.status)} value={job.status} />
      </div>
      <p className="mt-2 text-xs text-fieldiq-text">
        {plannedRange(job.plannedStartOn, job.plannedEndOn)} · Crew {displayValue(job.crewCount ?? undefined)} · Logs{' '}
        {displayValue(job.shiftLogCount ?? undefined)}
      </p>
    </>
  );
}
